import { useT, useLang, pickLocalized } from '../i18n';
import {
  profile,
  intro,
  expertise,
  experiences,
  education,
} from '../data/about';

export default function About() {
  const t = useT();
  const { lang } = useLang();

  return (
    <section className="flex max-w-4xl flex-col gap-8">
      <header className="flex flex-col gap-4 rounded-xl border border-neutral-200 bg-white p-6 shadow-soft transition-shadow duration-200 hover:shadow-float md:p-8">
        <h1 className="text-4xl md:text-5xl font-semibold leading-tight text-cocoa">
          {pickLocalized(profile.name, lang)}
        </h1>
        <p className="text-lg text-coffee font-mono">
          {pickLocalized(profile.tagline, lang)}
        </p>
        <ul className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm font-mono text-coffee">
          <li>
            <span className="text-coffee/70">{t('about.contact.emailLabel')}</span>{' '}
            <a
              href={`mailto:${profile.email}`}
              className="rounded text-cocoa transition hover:bg-neutral-100 hover:text-neutral-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15"
            >
              {profile.email}
            </a>
          </li>
          <li aria-hidden className="text-neutral-300">·</li>
          <li>
            <span className="text-coffee/70">{t('about.contact.phoneLabel')}</span>{' '}
            <a
              href={`tel:+86${profile.phone}`}
              className="rounded text-cocoa transition hover:bg-neutral-100 hover:text-neutral-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15"
            >
              {profile.phone}
            </a>
          </li>
          <li aria-hidden className="text-neutral-300">·</li>
          <li>
            <span className="text-coffee/70">
              {t('about.contact.locationLabel')}
            </span>{' '}
            <span className="text-cocoa">{pickLocalized(profile.location, lang)}</span>
          </li>
        </ul>
      </header>

      <section className="rounded-lg border border-neutral-200 bg-white p-6 shadow-soft transition-shadow duration-200 hover:shadow-float">
        <h2 className="text-sm font-mono uppercase tracking-wider text-coffee/70">
          {t('about.intro')}
        </h2>
        <p className="mt-3 text-coffee leading-relaxed">{pickLocalized(intro, lang)}</p>
      </section>

      <section className="rounded-lg border border-neutral-200 bg-white p-6 shadow-soft transition-shadow duration-200 hover:shadow-float">
        <h2 className="text-sm font-mono uppercase tracking-wider text-coffee/70">
          {t('about.expertise')}
        </h2>
        <ul className="mt-3 flex flex-col gap-2 text-coffee leading-relaxed">
          {expertise.map((item, i) => (
            <li key={i} className="flex gap-3">
              <span aria-hidden className="text-neutral-500 select-none">▸</span>
              <span>{pickLocalized(item, lang)}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="rounded-lg border border-neutral-200 bg-white p-6 shadow-soft transition-shadow duration-200 hover:shadow-float">
        <h2 className="text-sm font-mono uppercase tracking-wider text-coffee/70">
          {t('about.experience')}
        </h2>
        <ol className="mt-5 flex flex-col gap-8 border-l border-neutral-200 pl-6">
          {experiences.map((exp, i) => (
            <li key={i} className="flex flex-col gap-3 relative">
              <span
                aria-hidden
                className="absolute -left-[29px] top-2 w-2 h-2 rounded-full bg-neutral-900 shadow-sm"
              />
              <div className="flex flex-col">
                <h3 className="text-lg font-semibold text-cocoa">
                  {pickLocalized(exp.company, lang)}
                </h3>
                <p className="text-sm text-coffee">
                  {pickLocalized(exp.role, lang)}
                </p>
                <p className="text-xs font-mono text-coffee/70 mt-1">
                  {pickLocalized(exp.period, lang)} · {pickLocalized(exp.location, lang)}
                </p>
              </div>
              <div className="flex flex-col gap-4">
                {exp.groups.map((group, gi) => (
                  <div key={gi} className="flex flex-col gap-1.5">
                    {group.heading && (
                      <h4 className="text-xs font-mono uppercase tracking-wider text-coffee">
                        {pickLocalized(group.heading, lang)}
                      </h4>
                    )}
                    <ul className="flex flex-col gap-1.5 text-sm text-coffee leading-relaxed">
                      {group.bullets.map((b, bi) => (
                        <li key={bi} className="flex gap-2.5">
                          <span aria-hidden className="text-neutral-400 select-none">·</span>
                          <span>{pickLocalized(b, lang)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="rounded-lg border border-neutral-200 bg-white p-6 shadow-soft transition-shadow duration-200 hover:shadow-float">
        <h2 className="text-sm font-mono uppercase tracking-wider text-coffee/70">
          {t('about.education')}
        </h2>
        <ol className="mt-5 flex flex-col gap-6 border-l border-neutral-200 pl-6">
          {education.map((edu, i) => (
            <li key={i} className="flex flex-col gap-2 relative">
              <span
                aria-hidden
                className="absolute -left-[29px] top-2 w-2 h-2 rounded-full bg-neutral-900 shadow-sm"
              />
              <h3 className="text-lg font-semibold text-cocoa">
                {pickLocalized(edu.school, lang)}
              </h3>
              <p className="text-sm text-coffee">{pickLocalized(edu.degree, lang)}</p>
              <p className="text-xs font-mono text-coffee/70">
                {pickLocalized(edu.period, lang)} · {pickLocalized(edu.location, lang)}
              </p>
              {edu.notes && (
                <p className="text-sm text-coffee leading-relaxed mt-1">
                  {pickLocalized(edu.notes, lang)}
                </p>
              )}
            </li>
          ))}
        </ol>
      </section>
    </section>
  );
}
